import { useState, useEffect } from "react";

import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Became Possible components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Became Possible context
import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar,
  setSidenavColor,
  setDarkMode,
} from "context";
import { useDispatch, useSelector } from "react-redux";
import { getUserActivitiesAction } from "store/features/authSlice";
import { setRowsNum } from "store/features/coreSlice";
import OrdersOverview from "components/UI/OrdersOverview";

// Custom styles for the Configurator
import ConfiguratorRoot from "./ConfiguratorRoot";

function Configurator() {
  const rDispatch = useDispatch();
  const [controller, dispatch] = useMaterialUIController();
  const {
    openConfigurator,
    fixedNavbar,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    table,
  } = controller;
  const { usersLogsDetail, userInfo } = useSelector((state) => state.auth);
  const [disabled, setDisabled] = useState(false);
  const [tableName, setTableName] = useState(null);
  const [title, setTitle] = useState("config");
  const sidenavColors = ["primary", "dark", "info", "success", "warning", "error"];

  const tablesNames = [
    { table: "directus_users", name: "insert-user" },
    { table: "job_title_routes", name: "permissions" },
    { table: "libraries", name: "insert-library" },
    { table: "books", name: "insert-book" },
    { table: "buying", name: "buying" },
    { table: "stores", name: "stores" },
    { table: "proposals", name: "proposal" },
    { table: "distributions", name: "distribution" },
    { table: "inventory", name: "inventory" },
    { table: "returns", name: "returns" },
    { table: "gifts", name: "gifts" },
    { table: "profile", name: "profile-overview" },
    { table: "config", name: "config" },
    { table: "auth", name: "basic" },
    { table: "auth", name: "cover" },
    { table: "profile", name: "profile" },
    { table: "profile", name: "settings" },
    { table: "works", name: "works" },
    { table: "works", name: "tracking" },
    { table: "researches", name: "researches" },
    { table: "accounts", name: "insert-account" },
  ];

  useEffect(() => {
    if (table) {
      setTableName(
        tablesNames.filter((x) => x.name === table)[0]
          ? tablesNames.filter((x) => x.name === table)[0].table
          : "config"
      );
      setTitle(
        tablesNames.filter((x) => x.name === table)[0] &&
          tablesNames.filter((x) => x.name === table)[0].name
      );
    } else {
      setTableName("config");
    }
  }, [table]);

  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  // const handleSimbleLayout = () => {
  //   setSimbleLayoutCheck(!simbleLayoutCheck);
  //   rDispatch(setSimbleLayout(!simbleLayout));
  // };
  const handleCloseConfigurator = () =>
    setOpenConfigurator(dispatch, { value: false, table: null });

  const handleTransparentSidenav = () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
  };
  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
  };
  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
  };
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);
  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  // sidenav type buttons styles
  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  // sidenav type active button styles
  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main,
    },
  });

  useEffect(() => {
    if (tableName && table) {
      if (tableName && tableName !== "profile") {
        rDispatch(getUserActivitiesAction({ collection: tableName }));
      } else if (tableName) rDispatch(getUserActivitiesAction({ userId: userInfo.id }));
    }
  }, [tableName]);

  const renderItem = () => {
    let r;
    if (tableName === "config")
      r = (
        <MDBox pt={0.5} pb={3} px={3}>
          <MDBox>
            <MDTypography variant="h6">Sidenav Colors</MDTypography>
            <MDBox mb={0.5}>
              {sidenavColors.map((color) => (
                <IconButton
                  key={color}
                  sx={({
                    borders: { borderWidth },
                    palette: { white, dark, background },
                    transitions,
                  }) => ({
                    width: "24px",
                    height: "24px",
                    padding: 0,
                    border: `${borderWidth[1]} solid ${darkMode ? background.sidenav : white.main}`,
                    borderColor: () => {
                      let borderColorValue = sidenavColor === color && dark.main;

                      if (darkMode && sidenavColor === color) {
                        borderColorValue = white.main;
                      }

                      return borderColorValue;
                    },
                    transition: transitions.create("border-color", {
                      easing: transitions.easing.sharp,
                      duration: transitions.duration.shorter,
                    }),
                    backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                      linearGradient(gradients[color].main, gradients[color].state),

                    "&:not(:last-child)": {
                      mr: 1,
                    },

                    "&:hover, &:focus, &:active": {
                      borderColor: darkMode ? white.main : dark.main,
                    },
                  })}
                  onClick={() => setSidenavColor(dispatch, color)}
                />
              ))}
            </MDBox>
          </MDBox>

          <MDBox mt={3} lineHeight={1}>
            <MDTypography variant="h6">Sidenav Type</MDTypography>
            <MDTypography variant="button" color="text">
              Choose between different sidenav types.
            </MDTypography>

            <MDBox
              sx={{
                display: "flex",
                mt: 2,
                mr: 1,
              }}
            >
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleDarkSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  !transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                Dark
              </MDButton>
              <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
                <MDButton
                  color="dark"
                  variant="gradient"
                  onClick={handleTransparentSidenav}
                  disabled={disabled}
                  fullWidth
                  sx={
                    transparentSidenav && !whiteSidenav
                      ? sidenavTypeActiveButtonStyles
                      : sidenavTypeButtonsStyles
                  }
                >
                  Transparent
                </MDButton>
              </MDBox>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleWhiteSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                White
              </MDButton>
            </MDBox>
          </MDBox>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
            lineHeight={1}
          >
            <MDTypography variant="h6">Navbar Fixed</MDTypography>

            <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
          </MDBox>
          <Divider />
          <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
            <MDTypography variant="h6">Light / Dark</MDTypography>

            <Switch checked={darkMode} onChange={handleDarkMode} />
          </MDBox>
          {/* <Divider />
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
            lineHeight={1}
          >
            <MDTypography variant="h6">Simble layout</MDTypography>

            <Switch checked={simbleLayoutCheck} onChange={handleSimbleLayout} />
          </MDBox> */}
          <Divider />

          <MDBox mt={3} lineHeight={1}>
            <MDTypography variant="h6">Rows number</MDTypography>
            <MDTypography variant="button" color="text">
              Choose number of rows.
            </MDTypography>

            <MDBox
              sx={{
                display: "flex",
                mt: 2,
                mr: 1,
                justifyContent: "space-between",
              }}
            >
              <MDButton
                color="dark"
                variant="gradient"
                onClick={() => rDispatch(setRowsNum(10))}
                disabled={disabled}
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                10
              </MDButton>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={() => rDispatch(setRowsNum(25))}
                disabled={disabled}
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                25
              </MDButton>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={() => rDispatch(setRowsNum(50))}
                disabled={disabled}
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                50
              </MDButton>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={() => rDispatch(setRowsNum(100))}
                disabled={disabled}
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                100
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    else if (usersLogsDetail)
      r = (
        <MDBox pt={0.5} pb={3} px={3}>
          <OrdersOverview title="" data={usersLogsDetail} />
        </MDBox>
      );
    else
      r = (
        <MDBox pt={0.5} pb={3} px={3}>
          <MDBox>
            <MDTypography variant="h6">Sidenav Colors</MDTypography>
            <MDBox mb={0.5}>
              {sidenavColors.map((color) => (
                <IconButton
                  key={color}
                  sx={({
                    borders: { borderWidth },
                    palette: { white, dark, background },
                    transitions,
                  }) => ({
                    width: "24px",
                    height: "24px",
                    padding: 0,
                    border: `${borderWidth[1]} solid ${darkMode ? background.sidenav : white.main}`,
                    borderColor: () => {
                      let borderColorValue = sidenavColor === color && dark.main;

                      if (darkMode && sidenavColor === color) {
                        borderColorValue = white.main;
                      }

                      return borderColorValue;
                    },
                    transition: transitions.create("border-color", {
                      easing: transitions.easing.sharp,
                      duration: transitions.duration.shorter,
                    }),
                    backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                      linearGradient(gradients[color].main, gradients[color].state),

                    "&:not(:last-child)": {
                      mr: 1,
                    },

                    "&:hover, &:focus, &:active": {
                      borderColor: darkMode ? white.main : dark.main,
                    },
                  })}
                  onClick={() => setSidenavColor(dispatch, color)}
                />
              ))}
            </MDBox>
          </MDBox>

          <MDBox mt={3} lineHeight={1}>
            <MDTypography variant="h6">Sidenav Type</MDTypography>
            <MDTypography variant="button" color="text">
              Choose between different sidenav types.
            </MDTypography>

            <MDBox
              sx={{
                display: "flex",
                mt: 2,
                mr: 1,
              }}
            >
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleDarkSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  !transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                Dark
              </MDButton>
              <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
                <MDButton
                  color="dark"
                  variant="gradient"
                  onClick={handleTransparentSidenav}
                  disabled={disabled}
                  fullWidth
                  sx={
                    transparentSidenav && !whiteSidenav
                      ? sidenavTypeActiveButtonStyles
                      : sidenavTypeButtonsStyles
                  }
                >
                  Transparent
                </MDButton>
              </MDBox>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleWhiteSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                White
              </MDButton>
            </MDBox>
          </MDBox>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
            lineHeight={1}
          >
            <MDTypography variant="h6">Navbar Fixed</MDTypography>

            <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
          </MDBox>
          <Divider />
          <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
            <MDTypography variant="h6">Light / Dark</MDTypography>

            <Switch checked={darkMode} onChange={handleDarkMode} />
          </MDBox>
          {/* <Divider />
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={3}
            lineHeight={1}
          >
            <MDTypography variant="h6">Simble layout</MDTypography>

            <Switch checked={simbleLayoutCheck} onChange={handleSimbleLayout} />
          </MDBox> */}
          <Divider />

          <MDBox mt={3} lineHeight={1}>
            <MDTypography variant="h6">Rows number</MDTypography>
            <MDTypography variant="button" color="text">
              Choose number of rows.
            </MDTypography>

            <MDBox
              sx={{
                display: "flex",
                mt: 2,
                mr: 1,
                justifyContent: "space-between",
              }}
            >
              <MDButton
                color="dark"
                variant="gradient"
                onClick={() => rDispatch(setRowsNum(10))}
                disabled={disabled}
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                10
              </MDButton>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={() => rDispatch(setRowsNum(25))}
                disabled={disabled}
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                25
              </MDButton>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={() => rDispatch(setRowsNum(50))}
                disabled={disabled}
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                50
              </MDButton>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={() => rDispatch(setRowsNum(100))}
                disabled={disabled}
                sx={
                  whiteSidenav && !transparentSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                100
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    return r;
  };

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <MDBox>
          <MDTypography variant="h5">
            {tableName !== "config" ? "System Configurator" : "config"}
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {tableName !== "config" ? "See other users activities." : "Dashboard configration"}
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {title}
          </MDTypography>
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <Divider />
      {renderItem()}
    </ConfiguratorRoot>
  );
}

export default Configurator;
